// src/components/ModuleAccessPicker.tsx
// Выбор доступа к модулям (только для superadmin)
import React from 'react';
import { LayoutDashboard, GraduationCap, BarChart3, Briefcase, Users, Wallet, FileText, Database } from 'lucide-react';

export interface ModuleAccess {
  canViewSalesCRM?: boolean;
  canViewHRM?: boolean;
  canViewAccountingCRM?: boolean;
  canViewHRDocuments?: boolean;
  canViewDataDriven?: boolean;
}

interface Props {
  value: ModuleAccess;
  onChange: (next: ModuleAccess) => void;
  disabled?: boolean;
}

const BASE_MODULES = [
  { key: '_dashboard', label: 'Дашборд', icon: <LayoutDashboard size={16} />, desc: 'Главная страница', color: '#2575fc' },
  { key: '_elearning', label: 'Обучение', icon: <GraduationCap size={16} />, desc: 'Курсы и тесты', color: '#9b59b6' },
  { key: '_bi', label: 'BI Дашборды', icon: <BarChart3 size={16} />, desc: 'Аналитика', color: '#28a745' },
];

const OPTIONAL_MODULES: { key: keyof ModuleAccess; label: string; icon: any; desc: string; color: string }[] = [
  { key: 'canViewSalesCRM', label: 'CRM Продажи', icon: <Briefcase size={16} />, desc: 'Сделки, воронки, задачи', color: '#2575fc' },
  { key: 'canViewHRM', label: 'HRM', icon: <Users size={16} />, desc: 'Кадры, найм, рекрутинг', color: '#6a11cb' },
  { key: 'canViewAccountingCRM', label: 'Бухгалтерия', icon: <Wallet size={16} />, desc: 'Счета, платежи, контрагенты', color: '#10b981' },
  { key: 'canViewHRDocuments', label: 'КДП', icon: <FileText size={16} />, desc: 'Кадровые документы, онбординг', color: '#f59e0b' },
];

const ModuleAccessPicker: React.FC<Props> = ({ value, onChange, disabled = false }) => {
  const toggle = (key: keyof ModuleAccess) => {
    if (disabled) return;
    const next = { ...value, [key]: !value[key] };
    // Авто-логика: HRM → Data-Driven
    if (key === 'canViewHRM') {
      next.canViewDataDriven = !!next.canViewHRM;
    }
    onChange(next);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Базовые — всегда включены */}
      <div>
        <div style={sectionTitle}>✅ Всегда доступно</div>
        <div style={grid}>
          {BASE_MODULES.map(m => (
            <div key={m.key} style={{ ...card, opacity: 0.75, cursor: 'not-allowed' }} title="Всегда доступно">
              <div style={{ ...checkbox, background: m.color, borderColor: m.color }}>
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: m.color, fontSize: 13, fontWeight: 700 }}>
                  {m.icon} {m.label}
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{m.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Опциональные */}
      <div>
        <div style={sectionTitle}>⚙️ Выберите модули</div>
        <div style={grid}>
          {OPTIONAL_MODULES.map(m => {
            const checked = !!value[m.key];
            return (
              <button
                key={String(m.key)}
                type="button"
                onClick={() => toggle(m.key)}
                disabled={disabled}
                style={{
                  ...card,
                  border: checked ? `2px solid ${m.color}` : '2px solid transparent',
                  background: checked ? `${m.color}10` : '#fff',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.6 : 1,
                }}
              >
                <div style={{
                  ...checkbox,
                  background: checked ? m.color : '#fff',
                  borderColor: checked ? m.color : '#cbd5e1',
                }}>
                  {checked && (
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: checked ? m.color : '#475569', fontSize: 13, fontWeight: 700 }}>
                    {m.icon} {m.label}
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{m.desc}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Info про авто-логику */}
      <div style={{
        padding: 10, borderRadius: 10,
        background: 'rgba(37,117,252,0.06)',
        display: 'flex', gap: 8, alignItems: 'flex-start',
        fontSize: 11, color: '#475569', lineHeight: 1.5,
      }}>
        <Database size={14} color="#2575fc" style={{ marginTop: 2, flexShrink: 0 }} />
        <div>
          <b>Data-Driven</b> включается автоматически вместе с <b>HRM</b>.
          Базовые модули (Дашборд, Обучение, BI) доступны всегда.
        </div>
      </div>
    </div>
  );
};

const sectionTitle: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, color: '#64748b',
  textTransform: 'uppercase', letterSpacing: 0.5,
  marginBottom: 8,
};

const grid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: 8,
};

const card: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10,
  padding: 12, borderRadius: 12,
  background: '#fff',
  border: '2px solid transparent',
  boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
  fontFamily: 'inherit',
  transition: 'all 0.15s',
};

const checkbox: React.CSSProperties = {
  width: 18, height: 18, borderRadius: 6,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  border: '2px solid #cbd5e1', flexShrink: 0,
};

export default ModuleAccessPicker;
